export interface MvpPath {
  name: string
  desc: string
}
export interface FirstFiveSlot {
  num: string
  title: string
  desc: string
}
export interface RecipeCategory {
  title: string
  desc: string
  /** Marca la categoría destacada (clásicos argentinos). */
  highlight?: boolean
}
export interface Bono {
  num: string
  name: string
  desc: string
  /** Qué desbloquea dentro del sistema. */
  role: string
  cover?: string
}
export interface Faq {
  q: string
  a: string
}

/**
 * Formato monetario único de la landing: siempre "$00.000".
 */
export const PRICE = {
  previous: '$24.990',
  current: '$14.990',
  note: 'Precio en pesos argentinos · Pago único',
} as const

export const PRODUCT_FACTS: string[] = [
  '100 recetas',
  '+160 páginas',
  'Clásicos argentinos',
  '+5 bonos',
]

/** Hoy → Con Postres que Venden™ (única sección de problema/transformación). */
export const TRANSFORM_BEFORE: string[] = [
  'Guardás recetas y nunca arrancás.',
  'No sabés qué conviene ofrecer.',
  'Ponés precios a ojo.',
  'Comprás ingredientes de más.',
  'No tenés nada armado para mostrar.',
]

export const TRANSFORM_AFTER: string[] = [
  'Tenés 5 recetas elegidas con criterio.',
  'Sabés qué comprar y en qué orden producir.',
  'Calculás tus costos y tu precio.',
  'Tenés tu menú y tus fotos listas.',
  'Sabés a quién ofrecerle primero.',
]

/** Criterios con los que el Método M.V.P.™ selecciona los productos iniciales. */
export const MVP_CRITERIA: string[] = [
  'Inversión inicial',
  'Facilidad',
  'Ingredientes compartidos',
  'Producción',
  'Conservación',
  'Traslado',
  'Atractivo visual',
  'Potencial de venta',
]

/** Los tres caminos del Método M.V.P.™ */
export const MVP_PATHS: MvpPath[] = [
  {
    name: 'MVP Económico',
    desc: 'Arrancás con la menor inversión posible, aprovechando ingredientes compartidos y lo que ya tenés en tu cocina.',
  },
  {
    name: 'MVP Sin Complicaciones',
    desc: 'Pocas técnicas, tiempos cortos y recetas que perdonan errores. Ideal si nunca vendiste nada.',
  },
  {
    name: 'MVP Alto Valor',
    desc: 'Postres de mayor valor percibido y mejor presentación, para un ticket más alto por pedido.',
  },
]

/** Los 5 roles que forman el primer menú vendible. */
export const FIRST_FIVE: FirstFiveSlot[] = [
  { num: '01', title: 'Un clásico argentino', desc: 'El postre que la gente ya conoce y busca.' },
  {
    num: '02',
    title: 'Uno de alta rotación',
    desc: 'Fácil de producir en tanda y vender con frecuencia.',
  },
  {
    num: '03',
    title: 'Un formato individual',
    desc: 'Porción o vasito, ideal para pedidos pequeños.',
  },
  { num: '04', title: 'Uno de alto valor', desc: 'Una opción con mayor valor percibido.' },
  {
    num: '05',
    title: 'Un armado para regalar',
    desc: 'Caja o combo para aumentar el valor del pedido.',
  },
]

/** Cómo están agrupadas las 100 recetas. */
export const RECIPE_CATEGORIES: RecipeCategory[] = [
  {
    title: 'Clásicos argentinos',
    desc: 'Alfajores, chocotorta, pastafrola, budines y los postres que acá ya se conocen y se buscan.',
    highlight: true,
  },
  { title: 'Brownies, cookies y bocaditos', desc: 'Alta rotación y fáciles de trasladar.' },
  { title: 'Tortas y porciones', desc: 'Rinden por tanda y se venden por porción.' },
  { title: 'Postres en vaso', desc: 'Individuales, prolijos y muy visuales.' },
  { title: 'Budines y muffins', desc: 'Larga conservación y producción simple.' },
  { title: 'Opciones premium', desc: 'Mayor valor percibido para subir el ticket.' },
]

/** Los 5 bonos: cada uno destraba un paso del sistema. */
export const BONOS: Bono[] = [
  {
    num: '01',
    name: 'Calculadora de Costos Dinámica™',
    role: 'Para no cobrar a ojo',
    desc: 'Cargás tus ingredientes y sabés cuánto te cuesta cada postre y a qué precio conviene venderlo.',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/1ea62d41-d3dc-49c1-af37-be3433048f71.jpg',
  },
  {
    num: '02',
    name: 'Cero Desperdicio™',
    role: 'Para no perder plata',
    desc: 'Organizás insumos, conservación y freezer para que nada se te eche a perder entre tanda y tanda.',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/a381c1f6-ba38-4870-bc62-42b56df5f511.jpg',
  },
  {
    num: '03',
    name: 'Menú que Vende™',
    role: 'Para tener algo que mostrar',
    desc: 'Convertís tus 5 recetas elegidas en una oferta clara, linda y fácil de comprar.',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/6cefbb7e-fd40-408f-b2c1-847fef51c99c.jpg',
  },
  {
    num: '04',
    name: 'Fotos que Dan Hambre™',
    role: 'Para que se vea rico',
    desc: 'Mejorás las fotos de tus postres con el celular y luz natural, sin comprar nada.',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/4de4c27e-6d66-4b2e-9492-4bfa1ef4327b.jpg',
  },
  {
    num: '05',
    name: 'Tus Primeras 20 Ventas™',
    role: 'Para salir a ofrecer',
    desc: 'Plan práctico con WhatsApp, contactos cercanos, mensajes y acciones para empezar a mover pedidos.',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/b6628463-f06e-4a2b-b139-0f2cadd31474.jpg',
  },
]

/** Contenido del ebook principal, dentro de la sección de oferta. */
export const OFFER_INCLUDES: string[] = [
  'Más de 160 páginas',
  '100 recetas rentables, con clásicos argentinos y opciones premium',
  'Método M.V.P.™ — Menú Vendible de Partida',
  'Organización de cocina, compras y producción por tandas',
  'Conservación, traslado, packaging y costeo',
  '12 boxes y combos',
  'Plan de implementación de 7 días',
]

/** Lo que NO hace falta para empezar (se muestra una sola vez, junto a las FAQ). */
export const NOT_NEEDED: string[] = [
  'Experiencia vendiendo',
  'Título de pastelería',
  'Cocina profesional',
  'Equipamiento industrial',
  'Local a la calle',
  'Saber de marketing',
]

export const FAQS: Faq[] = [
  {
    q: '¿Necesito experiencia en pastelería?',
    a: 'No. Está pensado para principiantes y para personas con conocimientos básicos. Todo se explica paso a paso, en simple.',
  },
  {
    q: '¿Son recetas difíciles?',
    a: 'Hay recetas de distintos niveles, pero el Método M.V.P.™ te ayuda a empezar por las más simples: pocas técnicas y tiempos cortos.',
  },
  {
    q: '¿Necesito ingredientes o equipamiento especiales?',
    a: 'No. Está orientado a una cocina hogareña, con ingredientes de supermercado y utensilios accesibles. Muchas recetas comparten insumos para que compres menos.',
  },
  {
    q: '¿100 recetas no son demasiadas?',
    a: 'Las 100 están disponibles desde el primer día para que tengas variedad y puedas ir sumando. El método te ayuda a elegir las 5 con las que tiene más sentido empezar.',
  },
  {
    q: '¿Cómo recibo el producto?',
    a: 'En formato digital, inmediatamente después de la compra.',
  },
  {
    q: '¿Puedo verlo desde el celular?',
    a: 'Sí. Lo abrís desde el celular, la tablet o la computadora, y lo tenés siempre a mano en la cocina.',
  },
  {
    q: '¿Es un pago único?',
    a: 'Sí. Es una compra única de $14.990, sin suscripciones ni pagos mensuales.',
  },
  {
    q: '¿Los 5 bonos están incluidos?',
    a: 'Sí. Los 5 bonos forman parte de la compra, sin costo adicional.',
  },
]
